import { useTranslation } from "react-i18next";

import { faCheck, faMegaphone } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useGetListPersonTrackerMembershipQuery } from "@/pages/Private/redux/list/list-tracker-membership.api";

interface AddToTrackerFooterButtonProps {
	/** The lead on screen; nothing is asked or shown without one. */
	listPersonId?: number;
	/** The lead's company. A lead without one cannot be tracked, so it gets no button. */
	organizationId?: number | null;
	isLoading: boolean;
	/** Opens the tracker picker. */
	onAdd: () => void;
}

/**
 * The "Add to Tracker" / "Added to Tracker" button in the footer of Compose and Review to send.
 *
 * It asks the server whether the lead is tracked (PREC-4497). Both pages read their leads from
 * `GET list/all-prospects`, which carries no tracker rows, and the button used to wait for a
 * `prospect.trackerPersons` that only the lead's detail call ever brought, so in normal use it was
 * never shown. Until the answer is in, or when it cannot be read, no button is shown: a failed
 * lookup must not read as "not tracked".
 */
export const AddToTrackerFooterButton = ({
	listPersonId,
	organizationId,
	isLoading,
	onAdd,
}: AddToTrackerFooterButtonProps) => {
	const { t } = useTranslation();
	const canBeTracked = !!listPersonId && !!organizationId;
	// `currentData`, not `data`: after switching leads, `data` still holds the previous lead's answer.
	const { currentData } = useGetListPersonTrackerMembershipQuery(listPersonId ?? 0, {
		skip: !canBeTracked,
	});

	if (!canBeTracked || !currentData) {
		return null;
	}

	return (
		<div className="w-fit">
			{currentData.tracked ? (
				<Button
					color={ButtonColor.SUCCESS}
					disabled={true}
					image={<Icon className="mr-2" icon={faCheck} />}
					isLoading={isLoading}
					size={ButtonSize.XS}
					title={t("inbox.trackerFooter.added")}
				/>
			) : (
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					image={<Icon className="mr-2" icon={faMegaphone} />}
					isLoading={isLoading}
					size={ButtonSize.XS}
					title={t("inbox.trackerFooter.add")}
					onClick={onAdd}
				/>
			)}
		</div>
	);
};
